import React from 'react'
import { Search, Filter } from 'lucide-react';
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, TableBody, TableCell, TableFooter, Switch, TableHead, TablePagination, TableRow, Table, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon, PenBoxIcon, PlusIcon } from 'lucide-react';
import CustomButton from '../../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { format } from "date-fns";
import AddUpdateRefrigerantEmissionModal from '../../components/Masters/EmissionRefrigerantMaster/AddUpdateRefrigerantEmissionModal';

const EmissionRefrigerantMaster = () => {
  const dispatch = useDispatch();

  const [refrigerantEmission, setRefrigerantEmission] = useState([]);
  const [selectedRefrigerantEmission, setSelectedRefrigerantEmission] = useState(null);

  const [isAddUpdateRefrigerantModalOpen, setIsAddUpdateRefrigerantEmissionModalOpen] = useState(false);
  const [totalRefEmission, setTotalRefEmission] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Enhanced filters state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'created_date',
    sortOrder: 'ASC',
    fuelType: '',
    year: '',
    unit: ''
  });

  // Filter options (These would be populated from API or defined statically)
  const [filterOptions, setFilterOptions] = useState({
    fuelTypes: [],
    years: [],
    units: []
  });


  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [RefEmissionFactorToDelete, setRefEmissionFactorToDelete] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const handleEdit = (emission) => {
    setSelectedRefrigerantEmission(emission);
    setIsAddUpdateRefrigerantEmissionModalOpen(true);
  };


  // Get filter options separately from main data fetch
  const getFilterOptions = async () => {
    try {
      // Ideally, you would have a dedicated API endpoint for this
      const response = await api.get('/admin/getRefrigerantEmissionFilterOptions');

      if (response.data && response.data.status === "200") {
        setFilterOptions({
          fuelTypes: response.data.refrigerantTypes || [],
          years: response.data.years || [],
          units: response.data.units || []
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);

      // Fallback: Extract from current data if API call fails
      if (refrigerantEmission.length > 0) {
        const uniqueFuelTypes = [...new Set(refrigerantEmission.map(item => item.fuel_name))];
        const uniqueYears = [...new Set(refrigerantEmission.map(item => item.valid_year))];
        const uniqueUnits = [...new Set(refrigerantEmission.map(item => item.unit_name))];

        setFilterOptions({
          fuelTypes: uniqueFuelTypes,
          years: uniqueYears,
          units: uniqueUnits
        });
      }
    }
  };

  const GetAllRefrigerantEmission = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const response = await api.get('/admin/getRefrigerantEmission', {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          status: filterParams.status,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder,
          refrigerantType: filterParams.fuelType,
          year: filterParams.year,
          unit: filterParams.unit
        }
      });
      setRefrigerantEmission(response.data.data);
      setTotalRefEmission(response.data.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch refrigerant emission factors',
        type: 'error',
      }));
    }
  }

  const handleRemove = (id) => {
    setRefEmissionFactorToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setIsSubmitting(true);
    api.delete(`/admin/DeleteRefrigerantEmission/${RefEmissionFactorToDelete.id}`)
      .then(() => {
        dispatch(showNotification({
          message: 'Refrigerant Emission Factor deleted successfully',
          type: 'success',
        }));
        GetAllRefrigerantEmission();
      })
      .catch(() => {
        dispatch(showNotification({
          message: 'Failed to Refrigerant Emission Factor',
          type: 'error',
        }));
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setIsSubmitting(false);
      });
  };


  // Handle search with debounce
  const debouncedSearch = React.useCallback(
    debounce((value) => {
      setFilters(prev => ({ ...prev, search: value }));
      setPage(0); // Reset to first page when searching
    }, 500),
    []
  );

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setPage(0); // Reset to first page when filters change
    GetAllRefrigerantEmission(0, rowsPerPage, filters);
    setFilterDialogOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      status: '',
      sortBy: 'created_date',
      sortOrder: 'ASC',
      fuelType: '',
      year: '',
      unit: ''
    });
    setPage(0);
    setFilterDialogOpen(false);
  };

  // Handle filter dialog open
  const handleOpenFilterDialog = () => {
    // Ensure filter options are loaded when dialog opens
    if (filterOptions.fuelTypes.length === 0) {
      getFilterOptions();
    }
    setFilterDialogOpen(true);
  };

  const handleActivateDeactivate = async (user) => {
    try {
      setIsSubmitting(true);
      const response = await api.post(`/admin/activateDeactivateRefrigerantEmission/${user.id}`, {
        is_active: !user.is_active
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: user.is_active ? 'Refrigerant Emission Factor deactivated successfully' : 'Refrigerant Emission Factor activated successfully',
          type: "success"
        }));
        GetAllRefrigerantEmission();
      } else {
        dispatch(showNotification({
          message: user.is_active ? 'Failed to deactivate Refrigerant Emission Factor type' : 'Failed to activate Refrigerant Emission Factor',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error activating/deactivating refrigerant factor:', error);
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    GetAllRefrigerantEmission();
    getFilterOptions();
  }, [page, rowsPerPage, filters.sortBy, filters.sortOrder, filters.search,
    filters.status, filters.fuelType, filters.year, filters.unit]);

  return (
    <>
      <PageHeader title="Refrigerant Emission">
        <div className="flex justify-end gap-5 items-center mt-5">
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              value={searchInput}
              placeholder="Search Refrigerant Emission..."
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value)
                debouncedSearch(value); // Debounce the actual filter update
              }}
              className="min-w-[300px]"
            />

            {/* Filter Button */}
            <CustomButton
              className="bg-blue-600 hover:bg-blue-700 text-white"
              text={<><Filter size={18} /> Filters</>}
              click={handleOpenFilterDialog}
            />

            {/* Sort Dropdown */}
            <FormControl size="small" className="min-w-[200px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                label="Sort By"
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setFilters(prev => ({
                    ...prev,
                    sortBy: field,
                    sortOrder: order
                  }));
                }}
              >
                <MenuItem value="created_date-ASC">Date (Oldest First)</MenuItem>
                <MenuItem value="created_date-DESC">Date (Newest First)</MenuItem>
                <MenuItem value="fuel_name-ASC">Fuel Name (A-Z)</MenuItem>
                <MenuItem value="fuel_name-DESC">Fuel Name (Z-A)</MenuItem>
                <MenuItem value="emission_kgco2e-ASC">Emission (Low to High)</MenuItem>
                <MenuItem value="emission_kgco2e-DESC">Emission (High to Low)</MenuItem>
              </Select>
            </FormControl>

          </div>
          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Add Refrigerant Emission</>}
            click={() => setIsAddUpdateRefrigerantEmissionModalOpen(true)}
          />
        </div>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Refrigerant</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Emission KgCo2e Kyoto only</TableCell>
                <TableCell>Emission KgCo2e nonKyoto only</TableCell>
                <TableCell>Total Emission KgCo2e</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refrigerantEmission?.map((emission) => (
                <TableRow key={emission.id}>
                  <TableCell>{emission.refrigerant_name}</TableCell>
                  <TableCell>{emission.valid_year}</TableCell>
                  <TableCell>{emission.unit_name}</TableCell>
                  <TableCell>{emission.emission_kgco2e_kyoto_only > 0 ? emission.emission_kgco2e_kyoto_only : '--'} <span className='font-bold'>{emission.emission_kgco2e_kyoto_only > 0 ? 'kgco2e' : ''}</span></TableCell>
                  <TableCell>{emission.emission_kgco2e_nonkyoto_only > 0 ? emission.emission_kgco2e_nonkyoto_only : '--'}<span className='font-bold'>{emission.emission_kgco2e_nonkyoto_only > 0 ? 'kgco2e' : ''}</span></TableCell>
                  <TableCell>{emission.emission_kgco2e > 0 ? emission.emission_kgco2e : '--'}<span className='font-bold'>{emission.emission_kgco2e > 0 ? 'kgco2e' : ''}</span></TableCell>
                  <TableCell>
                    <Switch
                      checked={emission.is_active}
                      color='success'
                      onChange={(e) => handleActivateDeactivate(emission)}
                      disabled={isSubmitting}
                    />
                  </TableCell>
                  <TableCell>
                    {emission.created_date ? format(new Date(emission.created_date), "dd-MM-yyyy") : ""}
                  </TableCell>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(emission)}
                      sx={{
                        backgroundColor: '#16A34A',
                        color: 'white',
                        '&:hover': { backgroundColor: '#00bf46' },
                        p: '8px',
                      }}
                    >
                      <PenBoxIcon size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(emission)}
                      sx={{
                        backgroundColor: '#df3e3e',
                        color: 'white',
                        '&:hover': { backgroundColor: '#ff0000' },
                        p: '8px',
                      }}
                    >
                      <DeleteIcon size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={9}
                  count={totalRefEmission}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) =>
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* ADD EDIT MODAL */}
        <Dialog
          open={isAddUpdateRefrigerantModalOpen}
          onClose={() => { setIsAddUpdateRefrigerantEmissionModalOpen(false); setSelectedRefrigerantEmission(null) }}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedRefrigerantEmission ? "Edit Refrigerant Emission" : "Add New Refrigerant Emission"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdateRefrigerantEmissionModalOpen(false); setSelectedRefrigerantEmission(null) }}
            />
          </div>
          <DialogContent >
            <AddUpdateRefrigerantEmissionModal
              refetch={GetAllRefrigerantEmission}
              onClose={() => {
                setIsAddUpdateRefrigerantEmissionModalOpen(false);
                setSelectedRefrigerantEmission(null);
              }}
              countryId={selectedRefrigerantEmission?.id}
              initialData={selectedRefrigerantEmission}
            />
          </DialogContent>
        </Dialog>

        {/* DELETE DIALOG */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the fuel emission for {RefEmissionFactorToDelete?.refrigerant_name} year {RefEmissionFactorToDelete?.valid_year}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Cancel"
                disabled={isSubmitting}
                click={() => setDeleteDialogOpen(false)}
              />
              <CustomButton
                className="bg-red-600 hover:bg-red-700 text-white"
                click={confirmDelete}
                text='Delete'
                disabled={isSubmitting}
              />
            </div>
          </DialogActions>
        </Dialog>

        {/* Filters Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              Filter Fuel Emissions
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => setFilterDialogOpen(false)}
            />
          </div>
          <DialogContent>
            {filterOptions.fuelTypes.length === 0 && (
              <div className="text-center py-2">Loading filter options...</div>
            )}
            <div className="space-y-4 py-2">
              {/* Fuel Type Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Fuel Type</InputLabel>
                <Select
                  value={filters.fuelType}
                  label="Fuel Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, fuelType: e.target.value }))}
                >
                  <MenuItem value="">All Fuel Types</MenuItem>
                  {filterOptions.fuelTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Year Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  label="Year"
                  onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {filterOptions.years.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Unit Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select
                  value={filters.unit}
                  label="Unit"
                  onChange={(e) => setFilters(prev => ({ ...prev, unit: e.target.value }))}
                >
                  <MenuItem value="">All Units</MenuItem>
                  {filterOptions.units.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5 p-2">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Reset"
                click={resetFilters}
              />
              <CustomButton
                className="bg-blue-600 hover:bg-blue-700 text-white"
                text="Apply Filters"
                click={applyFilters}
              />
            </div>
          </DialogActions>
        </Dialog>
      </PageHeader>
    </>
  )
}

export default EmissionRefrigerantMaster